import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Row, Col, Container } from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { DetailBackButton } from '../../../Components/Common/Detail';
import { proHydroApi } from '../../../helpers/crude_api_helper';
import { formatForDisplay } from '../../../utils/dateformatpickr';

const renderJsonBlock = value => {
  if (!value) return <span className='text-muted'>-</span>;
  let parsed = value;
  try {
    parsed = typeof value === 'string' ? JSON.parse(value) : value;
  } catch (error) {
    parsed = value;
  }
  return (
    <pre
      className='bg-light p-2 rounded mb-0'
      style={{ whiteSpace: 'pre-wrap' }}
    >
      {typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)}
    </pre>
  );
};

const renderGeolocation = geo => {
  if (!geo || (!geo.latitude && !geo.longitude)) return 'Unknown';
  return `${geo.latitude}, ${geo.longitude} (±${geo.accuracy ?? 0}m)`;
};

const AuditDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  document.title = 'Audit Detail';

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchDetail = async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const result = await proHydroApi.get(`/v1/Audits/${id}`);
      setAudit(result);
    } catch (error) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>Loading...</div>
        </Container>
      </div>
    );
  }

  if (notFound || !audit) {
    return (
      <React.Fragment>
        <div className='page-content'>
          <Container fluid>
            <BreadCrumb title='Not Found' pageTitle='Audits' />
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody className='text-center py-5'>
                    <h5>Data tidak ditemukan</h5>
                    <Link
                      to='/crude/audit-log'
                      className='btn btn-light btn-sm mt-3'
                    >
                      <i className='ri-arrow-left-line align-middle me-2'></i>
                      Back to List
                    </Link>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }

  const heading = `${audit.actionType || ''} ${audit.entityName || ''}`.trim();

  const fieldRows = [
    [
      { label: 'Table Name', value: audit.tableName },
      { label: 'Entity ID', value: audit.entityId },
    ],
    [
      { label: 'Action Name', value: audit.actionName },
      { label: 'Client Application ID', value: audit.clientApplicationId },
    ],
    [
      { label: 'By', value: audit.createdBy },
      { label: 'From IP Address', value: audit.fromIpAddress },
    ],
    [
      {
        label: 'From Geolocation',
        value: renderGeolocation(audit.fromGeolocation),
      },
    ],
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title={heading || 'Audit Detail'} pageTitle='Audits' />
          <div className='dt-header__top mb-3'>
            <DetailBackButton
              label='Back to list'
              onClick={() => navigate('/crude/audit-log')}
            />
            <span className='text-muted'>
              On {formatForDisplay(audit.created)}
            </span>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h5 className='card-title mb-0'>{heading}</h5>
                </CardHeader>
                <CardBody>
                  {fieldRows.map((pair, i) => (
                    <Row key={i} className='border-bottom py-2'>
                      {pair.map((field, j) => (
                        <Col md={6} key={j}>
                          <div className='text-muted text-uppercase fs-12 fw-semibold'>
                            {field.label}
                          </div>
                          <div>{field.value || '-'}</div>
                        </Col>
                      ))}
                    </Row>
                  ))}

                  <Row className='py-2'>
                    <Col md={6}>
                      <div className='text-muted text-uppercase fs-12 fw-semibold mb-1'>
                        Old Values
                      </div>
                      {renderJsonBlock(audit.oldValues)}
                    </Col>
                    <Col md={6}>
                      <div className='text-muted text-uppercase fs-12 fw-semibold mb-1'>
                        New Values
                      </div>
                      {renderJsonBlock(audit.newValues)}
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AuditDetailView;
